/**
 * HighSpeedSubstitution — Basketball-speed substitution module.
 *
 * Two interaction models:
 *   A) Drag-and-drop: Drag an active player off the court OR drag a bench
 *      player onto an active player for an instant swap.
 *   B) Quick-tap: Tap an active player → a horizontal bench drawer slides
 *      up from the bottom → tap a bench player → instant swap.
 *
 * Animations ≤ 200ms. No confirmation dialogs.
 * Haptic feedback via navigator.vibrate when available.
 */
import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import type { Player, Team } from "../../types";
import BasketballCourtSVG from "../../../../components/BasketballCourtSVG";

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

/** Fixed 5v5 basketball court positions (x%, y%) — left half for home. */
const COURT_POSITIONS: [number, number][] = [
  [20, 50],  // PG — top of key
  [15, 25],  // SG — left wing
  [15, 75],  // SF — right wing
  [35, 35],  // PF — high post left
  [35, 65],  // C  — high post right
];

const COURT_WIDTH = 94; // ft (used for aspect ratio only)
const COURT_HEIGHT = 50;
const AVATAR_SIZE = 44; // px
const DRAG_THRESHOLD = 5; // px before counting as drag
const SWAP_ANIMATION_MS = 180;

/* ------------------------------------------------------------------ */
/*  Haptic helpers                                                     */
/* ------------------------------------------------------------------ */

const hapticLight = () => {
  try {
    navigator.vibrate?.(10);
  } catch { /* no-op on unsupported */ }
};

const hapticThud = () => {
  try {
    navigator.vibrate?.([20, 30, 40]);
  } catch { /* no-op */ }
};

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface HighSpeedSubstitutionProps {
  team: Team;
  allPlayers: Player[];
  onFieldIds: Set<string>;
  expelledPlayerIds?: Set<string>;
  onSwap: (playerOffId: string, playerOnId: string) => void;
  onClose: () => void;
  t: (key: string, fallback: string) => string;
}

/* ------------------------------------------------------------------ */
/*  Player Avatar                                                      */
/* ------------------------------------------------------------------ */

interface AvatarProps {
  player: Player;
  variant: "court" | "bench";
  isSelected?: boolean;
  isDragSource?: boolean;
  isDropTarget?: boolean;
  isVacant?: boolean;
  onPointerDown?: (e: React.PointerEvent) => void;
  onClick?: () => void;
  style?: React.CSSProperties;
  testId?: string;
}

const PlayerAvatar: React.FC<AvatarProps> = ({
  player,
  variant,
  isSelected = false,
  isDragSource = false,
  isDropTarget = false,
  isVacant = false,
  onPointerDown,
  onClick,
  style,
  testId,
}) => {
  const base =
    variant === "court"
      ? "bg-emerald-500 border-emerald-300 text-white shadow-lg shadow-emerald-500/30"
      : "bg-slate-600 border-slate-400 text-slate-200 shadow-md";

  const selected = isSelected
    ? "ring-2 ring-orange-400 ring-offset-2 ring-offset-slate-900 scale-110"
    : "";

  const dragSource = isDragSource ? "opacity-60 scale-95" : "";
  const dropTarget = isDropTarget
    ? "ring-2 ring-cyan-400 animate-pulse scale-105"
    : "";
  const vacant = isVacant
    ? "border-dashed border-2 border-amber-400 bg-amber-400/10"
    : "";

  return (
    <div
      data-testid={testId}
      data-player-id={player.id}
      className={`
        inline-flex items-center justify-center rounded-full
        border-2 font-bold text-sm select-none cursor-grab
        transition-all duration-150 ease-out
        ${base} ${selected} ${dragSource} ${dropTarget} ${vacant}
        hover:brightness-110 active:scale-95
      `}
      style={{
        width: AVATAR_SIZE,
        height: AVATAR_SIZE,
        touchAction: "none",
        ...style,
      }}
      onPointerDown={onPointerDown}
      onClick={onClick}
    >
      {player.jersey_number}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Bench Drawer (horizontal scroll at bottom)                         */
/* ------------------------------------------------------------------ */

interface BenchDrawerProps {
  players: Player[];
  isOpen: boolean;
  onSelect: (player: Player) => void;
  selectedCourtPlayerId: string | null;
  side: "home" | "away";
  t: (key: string, fallback: string) => string;
}

const BenchDrawer: React.FC<BenchDrawerProps> = ({
  players,
  isOpen,
  onSelect,
  selectedCourtPlayerId,
  t,
}) => (
  <div
    data-testid="bench-drawer"
    className={`
      fixed bottom-0 left-0 right-0 z-50
      bg-slate-900/95 backdrop-blur-sm border-t border-slate-700
      transition-transform duration-200 ease-out
      ${isOpen ? "translate-y-0" : "translate-y-full"}
    `}
    style={{ maxHeight: "40vh" }}
  >
    <div className="px-4 pt-3 pb-1 flex items-center justify-between">
      <span className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
        {t("substitution.selectBench", "Select substitute")}
      </span>
      {selectedCourtPlayerId && (
        <span className="text-xs text-orange-400 font-medium">
          {t("substitution.tapToSwap", "Tap to swap")}
        </span>
      )}
    </div>
    <div
      className="flex gap-3 overflow-x-auto px-4 pb-4 pt-1 snap-x snap-mandatory"
      data-testid="bench-drawer-list"
    >
      {players.map((p) => (
        <button
          key={p.id}
          type="button"
          data-testid={`bench-player-${p.id}`}
          onClick={() => onSelect(p)}
          className="
            flex flex-col items-center gap-1 snap-center shrink-0
            px-3 py-2 rounded-xl
            bg-slate-800 border border-slate-600
            hover:bg-slate-700 hover:border-cyan-500
            active:scale-95
            transition-all duration-150 ease-out
            min-w-[72px]
          "
        >
          <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-slate-600 border-2 border-slate-400 text-white font-bold text-sm">
            {p.jersey_number}
          </span>
          <span className="text-[10px] text-slate-300 truncate max-w-[64px]">
            {p.short_name || p.full_name}
          </span>
          <span className="text-[9px] text-slate-500 uppercase">
            {p.position}
          </span>
        </button>
      ))}
      {players.length === 0 && (
        <span className="text-sm text-slate-500 py-4">
          {t("substitution.noBench", "No bench players available")}
        </span>
      )}
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Bench Sidebar (vertical strip)                                     */
/* ------------------------------------------------------------------ */

interface BenchSidebarProps {
  players: Player[];
  side: "left" | "right";
  onDrop: (benchPlayerId: string) => void;
  draggedCourtPlayerId: string | null;
  t: (key: string, fallback: string) => string;
}

const BenchSidebar: React.FC<BenchSidebarProps> = ({
  players,
  side,
  onDrop: _onDrop,
  draggedCourtPlayerId,
  t,
}) => (
  <div
    data-testid={`bench-sidebar-${side}`}
    className={`
      flex flex-col items-center gap-2 py-3 px-2
      bg-slate-800/60 border-slate-700 overflow-y-auto
      ${side === "left" ? "border-r" : "border-l"}
      ${draggedCourtPlayerId ? "bg-red-900/20 border-red-500/40" : ""}
      transition-colors duration-150
    `}
    style={{ width: 64, minHeight: 200 }}
  >
    <span className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
      {t("substitutes", "Bench")}
    </span>
    {players.map((p) => (
      <div
        key={p.id}
        data-testid={`sidebar-bench-${p.id}`}
        className="flex flex-col items-center gap-0.5"
      >
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-slate-600 border border-slate-500 text-xs font-bold text-slate-200 cursor-grab active:scale-90 transition-transform duration-100">
          {p.jersey_number}
        </span>
        <span className="text-[8px] text-slate-500 uppercase">
          {p.position}
        </span>
      </div>
    ))}
    {players.length === 0 && (
      <span className="text-[9px] text-slate-600 mt-4">
        {t("substitution.emptyBench", "—")}
      </span>
    )}
  </div>
);

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */

const HighSpeedSubstitution: React.FC<HighSpeedSubstitutionProps> = ({
  team,
  allPlayers,
  onFieldIds,
  expelledPlayerIds,
  onSwap,
  onClose,
  t,
}) => {
  /* ---- Derived data ---- */
  const courtPlayers = useMemo(
    () => allPlayers.filter((p) => onFieldIds.has(p.id)),
    [allPlayers, onFieldIds],
  );

  const benchPlayers = useMemo(
    () =>
      allPlayers.filter(
        (p) => !onFieldIds.has(p.id) && !expelledPlayerIds?.has(p.id),
      ),
    [allPlayers, onFieldIds, expelledPlayerIds],
  );

  /* ---- Interaction state ---- */
  const [selectedCourtPlayerId, setSelectedCourtPlayerId] = useState<
    string | null
  >(null);
  const [draggingPlayerId, setDraggingPlayerId] = useState<string | null>(null);
  const [dropTargetPlayerId, setDropTargetPlayerId] = useState<string | null>(
    null,
  );
  const [vacantSlotIndex, setVacantSlotIndex] = useState<number | null>(null);
  const [swapFlash, setSwapFlash] = useState<string | null>(null);
  const [showDrawer, setShowDrawer] = useState(false);

  /* ---- Drag refs ---- */
  const dragRef = useRef<{
    startX: number;
    startY: number;
    hasMoved: boolean;
    playerId: string;
    source: "court" | "bench";
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  /* ---- Close drawer on Escape ---- */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (showDrawer) {
          setShowDrawer(false);
          setSelectedCourtPlayerId(null);
        } else {
          onClose();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showDrawer, onClose]);

  /* ---- Execute swap ---- */
  const executeSwap = useCallback(
    (playerOffId: string, playerOnId: string) => {
      hapticThud();
      setSwapFlash(playerOnId);
      onSwap(playerOffId, playerOnId);
      setSelectedCourtPlayerId(null);
      setShowDrawer(false);
      setDraggingPlayerId(null);
      setDropTargetPlayerId(null);
      setVacantSlotIndex(null);
      setTimeout(() => setSwapFlash(null), SWAP_ANIMATION_MS);
    },
    [onSwap],
  );

  /* ---- Quick-tap: select court player → open drawer ---- */
  const handleCourtPlayerTap = useCallback(
    (player: Player) => {
      if (expelledPlayerIds?.has(player.id)) return;
      hapticLight();
      setSelectedCourtPlayerId(player.id);
      setShowDrawer(true);
    },
    [expelledPlayerIds],
  );

  /* ---- Quick-tap: select bench player from drawer → swap ---- */
  const handleDrawerSelect = useCallback(
    (benchPlayer: Player) => {
      if (!selectedCourtPlayerId) return;
      executeSwap(selectedCourtPlayerId, benchPlayer.id);
    },
    [selectedCourtPlayerId, executeSwap],
  );

  /* ---- Drag: pointer down on court player ---- */
  const handleCourtPointerDown = useCallback(
    (e: React.PointerEvent, player: Player) => {
      if (expelledPlayerIds?.has(player.id)) return;
      e.preventDefault();
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        hasMoved: false,
        playerId: player.id,
        source: "court",
      };
      hapticLight();
    },
    [expelledPlayerIds],
  );

  /* ---- Drag: global pointer move ---- */
  useEffect(() => {
    const handleMove = (e: PointerEvent) => {
      if (!dragRef.current) return;
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      if (
        !dragRef.current.hasMoved &&
        Math.abs(dx) + Math.abs(dy) > DRAG_THRESHOLD
      ) {
        dragRef.current.hasMoved = true;
        setDraggingPlayerId(dragRef.current.playerId);
      }
      if (!dragRef.current.hasMoved) return;

      // Hit-test court players under pointer (for bench→court drag)
      const target = document.elementFromPoint(e.clientX, e.clientY);
      if (target) {
        const playerEl = (target as HTMLElement).closest?.(
          "[data-player-id]",
        ) as HTMLElement | null;
        const targetId = playerEl?.dataset.playerId ?? null;
        if (
          targetId &&
          targetId !== dragRef.current.playerId &&
          onFieldIds.has(targetId)
        ) {
          setDropTargetPlayerId(targetId);
        } else {
          setDropTargetPlayerId(null);
        }

        // If court player dragged into bench sidebar zone
        if (dragRef.current.source === "court") {
          const sidebarEl = (target as HTMLElement).closest?.(
            "[data-testid^='bench-sidebar']",
          );
          if (sidebarEl) {
            // Mark the court slot as vacant
            const courtIdx = courtPlayers.findIndex(
              (p) => p.id === dragRef.current!.playerId,
            );
            setVacantSlotIndex(courtIdx >= 0 ? courtIdx : null);
          } else {
            setVacantSlotIndex(null);
          }
        }
      }
    };

    const handleUp = () => {
      if (!dragRef.current) return;
      const { hasMoved, playerId, source } = dragRef.current;

      if (!hasMoved) {
        // It was a tap, not a drag
        const player = allPlayers.find((p) => p.id === playerId);
        if (player && source === "court") {
          handleCourtPlayerTap(player);
        }
      } else if (source === "bench" && dropTargetPlayerId) {
        // Bench player dragged onto a court player → instant swap
        executeSwap(dropTargetPlayerId, playerId);
      }
      // Court player dragged to bench sidebar doesn't auto-swap —
      // it marks the slot vacant for a follow-up bench drag.
      // Reset if no swap happened.
      if (!dropTargetPlayerId || source === "court") {
        setVacantSlotIndex(null);
      }

      dragRef.current = null;
      setDraggingPlayerId(null);
      setDropTargetPlayerId(null);
    };

    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
    };
  }, [
    onFieldIds,
    courtPlayers,
    allPlayers,
    dropTargetPlayerId,
    executeSwap,
    handleCourtPlayerTap,
  ]);

  /* ---- Close drawer on outside tap ---- */
  const handleBackdropClick = useCallback(() => {
    if (showDrawer) {
      setShowDrawer(false);
      setSelectedCourtPlayerId(null);
    }
  }, [showDrawer]);

  return (
    <div
      data-testid="high-speed-substitution"
      className="fixed inset-0 z-40 flex flex-col bg-slate-950/95 backdrop-blur-sm"
      ref={containerRef}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
        <div className="flex items-center gap-3">
          <span className="text-sm font-bold text-white uppercase tracking-wider">
            {team.short_name}
          </span>
          <span className="text-xs text-orange-400 font-medium">
            {t("substitution.quickMode", "Quick Sub")}
          </span>
        </div>
        <button
          type="button"
          data-testid="close-high-speed-sub"
          onClick={onClose}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700 transition-colors duration-100"
          aria-label="Close"
        >
          ✕
        </button>
      </div>

      {/* Main area: sidebars + court */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Left bench sidebar */}
        <BenchSidebar
          players={benchPlayers.slice(0, Math.ceil(benchPlayers.length / 2))}
          side="left"
          onDrop={() => {}}
          draggedCourtPlayerId={draggingPlayerId}
          t={t}
        />

        {/* Court area */}
        <div
          className="flex-1 relative"
          onClick={handleBackdropClick}
          data-testid="substitution-court"
        >
          {/* Court background */}
          <div className="absolute inset-0 flex items-center justify-center">
            <BasketballCourtSVG
              className="w-full h-full max-w-[600px] max-h-[360px]"
              courtColor="#c17929"
              lineColor="#ffffff"
              lineOpacity={0.19}
              lineWidth={0.3}
              data-testid="sub-court-svg"
            />
          </div>

          {/* Player dots on court */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div
              className="relative"
              style={{
                width: "min(100%, 600px)",
                height: "min(100%, 360px)",
                aspectRatio: `${COURT_WIDTH}/${COURT_HEIGHT}`,
              }}
            >
              {courtPlayers.map((player, idx) => {
                const pos = COURT_POSITIONS[idx % COURT_POSITIONS.length];
                const isVacant = vacantSlotIndex === idx;
                const isFlash = swapFlash === player.id;

                return (
                  <div
                    key={player.id}
                    className={`
                      absolute -translate-x-1/2 -translate-y-1/2
                      transition-all ease-out
                      ${isFlash ? "duration-[180ms] scale-125" : "duration-150"}
                    `}
                    style={{
                      left: `${pos[0]}%`,
                      top: `${pos[1]}%`,
                    }}
                  >
                    <PlayerAvatar
                      player={player}
                      variant="court"
                      isSelected={selectedCourtPlayerId === player.id}
                      isDragSource={draggingPlayerId === player.id}
                      isDropTarget={dropTargetPlayerId === player.id}
                      isVacant={isVacant}
                      onPointerDown={(e) => handleCourtPointerDown(e, player)}
                      testId={`court-player-${player.id}`}
                    />
                    <div className="text-center mt-0.5">
                      <span className="text-[9px] text-slate-300 font-medium leading-none">
                        {player.short_name || player.full_name.split(" ")[0]}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Hint text */}
          <div className="absolute bottom-3 left-0 right-0 text-center pointer-events-none">
            <span className="text-[10px] text-slate-500 uppercase tracking-wider">
              {t(
                "substitution.dragOrTap",
                "Tap a player or drag to swap",
              )}
            </span>
          </div>
        </div>

        {/* Right bench sidebar */}
        <BenchSidebar
          players={benchPlayers.slice(Math.ceil(benchPlayers.length / 2))}
          side="right"
          onDrop={() => {}}
          draggedCourtPlayerId={draggingPlayerId}
          t={t}
        />
      </div>

      {/* Bench drawer (quick-tap mode) */}
      <BenchDrawer
        players={benchPlayers}
        isOpen={showDrawer}
        onSelect={handleDrawerSelect}
        selectedCourtPlayerId={selectedCourtPlayerId}
        side="home"
        t={t}
      />

      {/* Drawer backdrop */}
      {showDrawer && (
        <div
          data-testid="drawer-backdrop"
          className="fixed inset-0 z-[45] bg-black/30"
          onClick={handleBackdropClick}
        />
      )}
    </div>
  );
};

export default HighSpeedSubstitution;
